/**
 * Build Present / Revised / Differential component rows for salary-revision arrears,
 * using the same structure engine as Assigned Structure (Employee profile breakdown).
 */
import { findApplicableSalaryStructure } from "./salaryStructureMatch";
import { buildEmployeeStructureBreakdown } from "./employeeStructureBreakdown";
import {
  normalizeSalaryStructureMethod,
  resolveStructureMode,
} from "./salaryStructureMethod";

function round2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

function listToMap(list) {
  const map = new Map();
  (list || []).forEach((row) => {
    const name = String(row?.name || "").trim();
    if (!name) return;
    const amt = Number(row.amount) || 0;
    map.set(name, (map.get(name) || 0) + amt);
  });
  return map;
}

function pushSection(out, section, presentMap, revisedMap, factor) {
  const names = [];
  const seen = new Set();
  const addNames = (map) => {
    map.forEach((_, name) => {
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      names.push(name);
    });
  };
  addNames(revisedMap);
  addNames(presentMap);

  // Present/Revised show the absolute day-portion amounts; Differential keeps the
  // signed factor (negative for mid-month clawback).
  const signedFactor = Number(factor) || 0;
  const absFactor = Math.abs(signedFactor);

  names.forEach((name) => {
    const presentMonthly = presentMap.get(name) || 0;
    const revisedMonthly = revisedMap.get(name) || 0;
    const present = round2(presentMonthly * absFactor);
    const revised = round2(revisedMonthly * absFactor);
    const differential = round2((revisedMonthly - presentMonthly) * signedFactor);
    if (
      Math.abs(present) < 0.005 &&
      Math.abs(revised) < 0.005 &&
      Math.abs(differential) < 0.005
    ) {
      return;
    }
    out.push({
      name,
      section,
      type: section,
      presentMonthly: round2(presentMonthly),
      revisedMonthly: round2(revisedMonthly),
      present,
      revised,
      differential,
      signedPresent: round2(presentMonthly * signedFactor),
      signedRevised: round2(revisedMonthly * signedFactor),
    });
  });
}

function buildComponentRows(presentBd, revisedBd, factor) {
  const empty = {
    earningsList: [],
    reimbursementsList: [],
    benefitsList: [],
    statutoryEmployerList: [],
    deductionsList: [],
    statutoryDeductionsList: [],
  };
  const p = presentBd || empty;
  const r = revisedBd || empty;
  const components = [];
  pushSection(
    components,
    "Earnings (E)",
    listToMap(p.earningsList),
    listToMap(r.earningsList),
    factor
  );
  pushSection(
    components,
    "Reimbursements (R)",
    listToMap(p.reimbursementsList),
    listToMap(r.reimbursementsList),
    factor
  );
  pushSection(
    components,
    "Employer contributions (B)",
    listToMap([...(p.benefitsList || []), ...(p.statutoryEmployerList || [])]),
    listToMap([...(r.benefitsList || []), ...(r.statutoryEmployerList || [])]),
    factor
  );
  pushSection(
    components,
    "Deductions (D)",
    listToMap([...(p.deductionsList || []), ...(p.statutoryDeductionsList || [])]),
    listToMap([...(r.deductionsList || []), ...(r.statutoryDeductionsList || [])]),
    factor
  );
  return components;
}

function sectionDiff(components, prefix) {
  return round2(
    (components || [])
      .filter((c) => String(c.section || "").startsWith(prefix))
      .reduce((s, c) => s + Number(c.differential || 0), 0)
  );
}

function earningsDiff(components) {
  return sectionDiff(components, "Earnings");
}

function deductionsDiff(components) {
  return sectionDiff(components, "Deductions");
}

/**
 * Resolve month factor for arrears: prefer attendance paid days ÷ calendar days.
 */
export function resolveMonthArrearsFactor(month = {}) {
  const cal =
    Number(month.attendanceCalendarDays ?? month.attendance?.calendarDays ?? month.calendarDays) ||
    0;
  const paid = Number(month.attendancePaidDays ?? month.attendance?.paidDays);
  const rows = Number(month.attendanceRows ?? month.attendance?.attendanceRows) || 0;
  if (rows > 0 && cal > 0 && Number.isFinite(paid)) {
    if (month.kind === "mid_month_clawback") {
      const f = Number(month.factor);
      return Number.isFinite(f) ? f : 0;
    }
    const payable = Number(month.payableDays);
    if (Number.isFinite(payable) && payable > 0 && payable < cal) {
      const proratedPaid = Math.min(Math.max(0, paid), payable);
      return round2(proratedPaid / cal);
    }
    return round2(Math.max(0, paid) / cal);
  }
  const f = Number(month.factor);
  return Number.isFinite(f) ? f : 0;
}

/** Sum of month segment factors (paid-days when available). */
export function sumArrearsDayFactor(months = []) {
  const total = (months || []).reduce((s, m) => s + resolveMonthArrearsFactor(m), 0);
  return Number.isFinite(total) && Math.abs(total) > 0.0001 ? round2(total) : 1;
}

/**
 * @returns {{
 *   components: Array,
 *   monthComponentTotals: Array,
 *   structureName: string|null,
 *   dayFactor: number,
 *   error: string|null,
 * }}
 */
export function buildSalaryRevisionArrearsComponents({
  employee,
  previousCtc,
  revisedCtc,
  structures = [],
  statutoryConfigJson = null,
  branchesList = [],
  orgSalaryMethod = "CTC",
  /** Sum of arrears month factors (full catch-up month = 1). */
  dayFactor = 1,
  /** Month segments from arrearsDetail (for month-wise tabs). */
  months = [],
  effectiveMonth = null,
  preferredStructureId = null,
}) {
  const prev = Number(previousCtc) || 0;
  const next = Number(revisedCtc) || 0;
  const factor = Number(dayFactor);
  const safeFactor = Number.isFinite(factor) && factor !== 0 ? factor : 1;

  if (!employee || (!prev && !next)) {
    return {
      components: [],
      monthComponentTotals: [],
      structureName: null,
      dayFactor: safeFactor,
      error: "Missing employee or CTC",
    };
  }

  let structure = null;
  if (preferredStructureId != null) {
    structure = (structures || []).find(
      (s) => Number(s.id) === Number(preferredStructureId)
    );
  }
  if (!structure) {
    structure = findApplicableSalaryStructure(employee, structures);
  }
  if (!structure) {
    return {
      components: [],
      monthComponentTotals: [],
      structureName: null,
      dayFactor: safeFactor,
      error: "No applicable salary structure for this employee",
    };
  }
  if (!Array.isArray(structure.components) || !structure.components.length) {
    return {
      components: [],
      monthComponentTotals: [],
      structureName: structure.name || null,
      dayFactor: safeFactor,
      error: `Structure "${structure.name || "—"}" has no components configured`,
    };
  }

  const method = normalizeSalaryStructureMethod(orgSalaryMethod);
  const mode = resolveStructureMode(
    method,
    structure.salaryType === "gross" ? "GROSS" : "CTC"
  );
  const salaryType = mode === "GROSS" ? "gross" : "ctc";

  const run = (annual) =>
    buildEmployeeStructureBreakdown({
      structure,
      annualAmount: annual,
      salaryType,
      employee,
      statutoryConfigJson,
      branchesList,
      effectiveMonth: effectiveMonth || undefined,
    });

  let presentBd;
  let revisedBd;
  try {
    presentBd = prev > 0 ? run(prev) : null;
    revisedBd = next > 0 ? run(next) : null;
  } catch (err) {
    return {
      components: [],
      monthComponentTotals: [],
      structureName: structure.name || null,
      dayFactor: safeFactor,
      error: err?.message || "Structure calculation failed",
    };
  }

  if (!presentBd && !revisedBd) {
    return {
      components: [],
      monthComponentTotals: [],
      structureName: structure.name || null,
      dayFactor: safeFactor,
      error: "Could not compute structure at previous/revised CTC",
    };
  }

  const components = buildComponentRows(presentBd, revisedBd, safeFactor);

  const monthComponentTotals = (months || []).map((seg) => {
    const mFactor = resolveMonthArrearsFactor(seg);
    const rows = buildComponentRows(presentBd, revisedBd, mFactor);
    return {
      payMonth: seg.payMonth,
      kind: seg.kind,
      note: seg.note,
      factor: mFactor,
      paidDays: seg.attendancePaidDays ?? seg.attendance?.paidDays,
      presentDays: seg.attendancePresentDays ?? seg.attendance?.presentDays,
      lopDays: seg.attendanceLopDays ?? seg.attendance?.lopDays,
      calendarDays:
        seg.attendanceCalendarDays ?? seg.attendance?.calendarDays ?? seg.calendarDays,
      components: rows,
      totalDifferential: earningsDiff(rows),
      grossDiff: earningsDiff(rows),
      deductionsDiff: deductionsDiff(rows),
      netPayDiff: round2(earningsDiff(rows) - deductionsDiff(rows)),
      monthCtcArrears: round2(((next - prev) / 12) * mFactor),
    };
  });

  return {
    components,
    monthComponentTotals,
    structureName: (revisedBd || presentBd)?.structureName || structure.name || null,
    dayFactor: safeFactor,
    error: components.length ? null : "Structure returned no component amounts",
  };
}
